import { Component, OnInit } from '@angular/core';
import { AccordionComponent } from '@ppl/ui';
import { CheckListService } from '../../../../services/check-list.service';
import { ChecklistResponse } from '../../../../interfaces/checklist.model';
import { ChecklistHelperService } from '../../../../services/checklist.helper.service';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { EnrollmentDataService } from '../../../../services/enrollment-data.service';
import { PROVIDER_TYPES } from '../../../../shared/constants/static-data.constants';
import { HealthAssessmentListComponent } from '../../health-assessment-list/health-assessment-list.component';
import { ListService } from '../../../../shared/services/list.service';
import { UserService } from '../../../../services/user.service';
import { SectionsService } from '../../../../services/sections.service';
import { StorageService } from '../../../../services/storage.service';
import { ToDoService } from '../../../../services/to-do.service';

@Component({
  selector: 'ppl-to-do-list',
  standalone: true,
  imports: [AccordionComponent, NzAlertComponent, HealthAssessmentListComponent],
  templateUrl: './to-do-list.component.html',
  styleUrl: './to-do-list.component.css'
})
export class ToDoListComponent implements OnInit {
  program: any;
  userId?: string | null;
  checkListData: any = [];
  isParticipantFlow = false;
  isProviderFlow = false;
  isPA = false;
  isIF = false;
  entry: string | null = '';
  prvdrAssociations: any = [];
  associationDetailsData: any = {};
  timesheets: any[] = [];
  serviceCredentials: any[] = [];
  linkedChecklists: any[] = [];

  constructor(private checkListService: CheckListService,
    private listService: ListService,
    private checklistHelperService: ChecklistHelperService,
    private enrollmentDataService: EnrollmentDataService, private userService: UserService,
    private sectionsService: SectionsService,
    private storageService: StorageService,
    private toDoService: ToDoService) { }

  ngOnInit(): void {
    const providerType = this.getEnrollmentType();
    if (providerType == 'provider') {
      this.isPA = true;
    }
    if (providerType == 'independentFacilitator') {
      this.isIF = true;
    }
    this.entry = this.userService.getLeadEntry() ?? '';
    this.program = this.userService.getSelectedProgram();
    this.userId = this.userService.getSelectedLead();
    this.isParticipantFlow = this.entry == 'participantEnrollment';
    this.isProviderFlow = this.entry == 'providerEnrollment';
    this.getChecklist();

    if (this.isIF) {
      this.getServiceCredentialStatus();
    }

    if (this.isParticipantFlow) {
      this.getTimesheetTodoList([{
        "jsonPath": "participantId",
        "value": this.userId,
        "operator": "Equal"
      }]);
    }

    if (!this.isParticipantFlow) {
      // Employer/Provider linked checklists are keyed by provider id, so provider flow only.
      this.getLinkedChecklists();
      this.getAssociationDetails();
      this.enrollmentDataService.getParticipantAssocChanged().subscribe(() => {
        this.getAssociationDetails();
      })
    }
  }

  getChecklist() {
    this.checkListService.getChecklist(this.program?.program_code, this.userId || '', this.isParticipantFlow).subscribe((data: any) => {
      if (data.responsedata) {
        const checklist: ChecklistResponse = data.responsedata
        if (checklist && checklist.checkList && checklist.checkList.length > 0) {
          this.checkListData = this.checklistHelperService.buildToDoChecklist(checklist.checkList.filter(e => e.checkListStatus == 'P'), this.isParticipantFlow, true);
        }
      }
    });
  }

  buildRequestPayload(pageName: string, searchParams: []) {
    if (searchParams.length == 0) {
      //handle the error here
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.program?.program_code,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: '1',
      searchParam: searchParams,
      orderByColumn: [],
      orderBy: [],
    };
    return payload;
  }

  getTimesheetTodoList(filters: any) {
    const processedPayload = this.buildRequestPayload(
      'consumerTodoList',
      filters
    );
    this.listService
      .getListData(processedPayload, 'claims')
      .subscribe((data: any) => {
        this.timesheets = data.responsedata;
      });
  }


  getServiceCredentialStatus() {
    this.toDoService.getServiceCredentialStatus(this.program?.program_code, this.userId || '')
      .subscribe((data: any) => {
        const entries = data?.responsedata ?? [];
        // Only pending service credentials are shown as to-do items.
        this.serviceCredentials = entries.filter((e: any) => (e.status || '').toLowerCase() === 'pending');
      });
  }

  getLinkedChecklists() {
    this.toDoService.getLinkedChecklists(this.program?.program_code, this.userId || '')
      .subscribe((entries: any[]) => {
        this.linkedChecklists = entries
          .map((entry: any) => ({
            ...entry,
            checklistGroups: (entry.checklistGroups ?? [])
              .filter((g: any) => !g.isCompleted)
              .map((g: any) => ({
                ...g,
                pendingSections: this.checklistHelperService.buildPendingSections(g)
              }))
          }))
          .filter((entry: any) => entry.checklistGroups.length > 0);
      });
  }

  getAssociationDetails() {
    const isParticipant = this.entry === 'participantEnrollment';
    const participantId = !isParticipant ? this.storageService.getSelfEmployerParticipantId() : undefined;
    this.enrollmentDataService.getAssociationsDetails(this.program.program_code, this.userId || '', isParticipant, false, participantId).subscribe((response: any) => {
      console.log(response.responsedata);
      const data = response.responsedata;
      if (isParticipant) {
        for (const key in PROVIDER_TYPES) {
          this.associationDetailsData[key] = data[key] || [];
        }
      } else {
        const filteredAssociations = data.participantAssociations.filter(
          (e: any) => e.associationStatus != 'Disassociated'
        );

        const uniqueAssociations: any[] = [
          ...new Map(
            filteredAssociations.map((assoc: any) => [
              `${assoc.participantId}-${assoc.providerType}`,
              assoc
            ])
          ).values()
        ];

        if (uniqueAssociations.length === 0) {
          this.prvdrAssociations = [];
          return;
        }

        this.toDoService.getAssociationChecklists(this.program.program_code, uniqueAssociations)
          .subscribe((associations: any[]) => {
            this.prvdrAssociations = associations
              .map((assoc: any) => ({
                ...assoc,
                checklistGroups: assoc.checklistGroups
                  .filter((g: any) => !g.isCompleted)
                  .map((g: any, index: number) => ({
                    ...g,
                    // The held-out items belong to the association as a whole, so they are folded into
                    // the first group only — repeating them per group would list them more than once.
                    pendingSections: this.checklistHelperService.buildPendingSections(
                      g,
                      index === 0 ? assoc.itemsExcludedFromGroups ?? [] : []
                    )
                  }))
              }))
              .filter((assoc: any) => assoc.checklistGroups.length > 0 || assoc.onDutyMealBreakPending || assoc.hcbsFormPending);
          });
      }
    });
  }

  getEnrollmentType() {
    const enrollTypeString = this.sectionsService.getEnrollmentType();
    const enrollType = enrollTypeString ? enrollTypeString : '';
    let enrollmentType = this.sectionsService.getProviderFlowType(enrollType);
    return enrollmentType;
  }
}
